/**
 * Que comprarle a cada proveedor.
 *
 * CUANTOS DIAS ALCANZA LO QUE QUEDA
 * ---------------------------------
 * Un producto entra cuando, al ritmo al que se vende, se agota antes de que
 * llegue lo que se pide hoy mas un colchon de una semana (0140). El ritmo sale
 * de los ultimos 90 dias contando solo los dias con stock, y la demora es la
 * del proveedor. Se pide para la demora mas 30 dias de ventas. Lo que rota poco
 * se repone de a uno y solo cuando se agota, sin alarma.
 *
 * Con menos de 14 dias de historia el ritmo no dice nada: entra lo vendido
 * desde la ultima compra cargada, como en la 0102. Donde hay ideal cargado
 * sigue valiendo, y se pide lo mayor.
 *
 * COMO SE LEE
 * -----------
 * Arriba, un resumen: cuanto hay para pedir, cuanto es urgente y cuanto espera.
 * Despues, una tarjeta por proveedor con lo que HAY QUE PEDIR y nada mas: cinco
 * columnas, cada importe en una sola moneda (la del costo del producto) y el
 * total del pedido al pie, en la moneda de la lista del proveedor, que es en la
 * que se le paga. La accion de cada tarjeta es una: «Pedir por WhatsApp».
 *
 * Lo urgente (se agota en menos de 5 dias habiles) va primero y se marca en
 * la columna «Hay», con color Y con texto: el color solo no alcanza.
 *
 * Lo que ya se pidio no se mezcla con lo que falta pedir: va en su propia
 * seccion al final, «Esperando la compra», hasta que se carga la factura. Si
 * tarda mas de una semana se resalta, porque hay que reclamarlo.
 *
 * El WhatsApp anota el pedido con el mismo clic: no es un paso aparte.
 */
import { useEffect, useState, type ChangeEvent } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Clock,
  Info,
  MessageCircle,
  RotateCcw,
  ShoppingCart,
  UserX,
} from "lucide-react";
import { formatearNumero } from "@tora/shared";

import { Importe } from "@/components/money";
import { linkWhatsapp, numeroWhatsapp } from "@/components/tienda/logica";
import { nombreCompleto } from "./nombre";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Cargando } from "@/components/ui/cargando";
import { Codigo } from "@/components/ui/codigo";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn, formatearFecha } from "@/lib/utils";
import { aNumero, type FilaReposicion, type PedidoReposicion } from "@/lib/tipos";

import {
  agruparReposicion,
  alcanceLegible,
  cantidadAPedir,
  DIAS_PEDIDO_DEMORADO,
  diasDelPedido,
  esperaPedido,
  mensajePedido,
  ritmoLegible,
  subtotalRenglon,
  totalEnLista,
  totalReposicion,
  type CantidadesPedido,
  type GrupoReposicion,
} from "./logica";

export interface ReposicionProps {
  filas: ReadonlyArray<FilaReposicion>;
  cargando?: boolean;
  /** Anota lo pedido al tocar «Pedir por WhatsApp». Sin esto, solo abre el chat. */
  onPedir?: (pedido: PedidoReposicion) => void;
  /** Deshace el pedido abierto de un producto. */
  onAnularPedido?: (productoId: number) => void;
  /** Abre la ficha de un producto: ahi se le asigna el proveedor. */
  onAbrirFicha?: (productoId: number) => void;
}

const CLAVE_MEMORIA = "tora.reposicion.cantidades";

function cantidadesRecordadas(): Record<number, number> {
  try {
    const crudo = localStorage.getItem(CLAVE_MEMORIA);
    const leido: unknown = crudo ? JSON.parse(crudo) : {};
    return leido !== null && typeof leido === "object" ? (leido as Record<number, number>) : {};
  } catch {
    return {};
  }
}

function recordarCantidades(c: CantidadesPedido): void {
  try {
    if (Object.keys(c).length === 0) localStorage.removeItem(CLAVE_MEMORIA);
    else localStorage.setItem(CLAVE_MEMORIA, JSON.stringify(c));
  } catch {
    // Modo privado o storage lleno: se pierde la memoria, no la pantalla.
  }
}

export function Reposicion({
  filas,
  cargando = false,
  onPedir,
  onAnularPedido,
  onAbrirFicha,
}: ReposicionProps): JSX.Element {
  const [cantidades, setCantidades] = useState<Record<number, number>>(cantidadesRecordadas);
  useEffect(() => recordarCantidades(cantidades), [cantidades]);

  const aPedir = filas.filter((f) => !esperaPedido(f));
  const esperando = filas.filter(esperaPedido);
  const todos = agruparReposicion(aPedir, cantidades);
  // Lo que no tiene proveedor no se le puede pedir a nadie: no suma al pedido,
  // va aparte con lo que falta para poder pedirlo.
  const grupos = todos.filter((g) => g.proveedorId !== null);
  const sinProveedor = todos.find((g) => g.proveedorId === null);
  const total = totalReposicion(grupos);
  const urgentes = grupos.reduce((n, g) => n + g.urgentes, 0);
  const hayCambios = aPedir.some((f) => cantidades[f.producto_id] !== undefined);

  function cambiar(f: FilaReposicion, n: number): void {
    setCantidades((c) => {
      const { [f.producto_id]: _, ...resto } = c;
      // Volver a lo sugerido es lo mismo que no haberlo tocado.
      return n === aNumero(f.cantidad_sugerida) ? resto : { ...resto, [f.producto_id]: n };
    });
  }

  // Lo que se acaba de pedir vuelve a lo sugerido: la base ya no lo sugiere, y
  // una correccion a mano vieja no tiene que sobrevivir al pedido.
  function pedir(g: GrupoReposicion): void {
    const items = g.filas
      .map((f) => ({ producto_id: f.producto_id, cantidad: cantidadAPedir(f, cantidades) }))
      .filter((i) => i.cantidad > 0);
    if (items.length === 0) return;
    onPedir?.({ proveedor_id: g.proveedorId, items });
    setCantidades((c) => {
      const resto = { ...c };
      for (const i of items) delete resto[i.producto_id];
      return resto;
    });
  }

  if (filas.length === 0) {
    return cargando ? (
      <Cargando texto="Calculando la reposición…" />
    ) : (
      <EstadoVacio
        icono={ShoppingCart}
        titulo="No hay nada para reponer: al ritmo de venta, lo que hay alcanza hasta que llegue un pedido."
      />
    );
  }

  return (
    <section className="space-y-4">
      {grupos.length > 0 ? (
        <Resumen
          productos={filas.length}
          unidades={total.unidades}
          proveedores={total.proveedores}
          urgentes={urgentes}
          esperando={esperando.length}
          sinProveedor={sinProveedor?.filas.length ?? 0}
          ars={total.ars}
          usd={total.usd}
          hayCambios={hayCambios}
          onRestablecer={() => setCantidades({})}
        />
      ) : (
        <EstadoVacio
          icono={ShoppingCart}
          titulo={
            sinProveedor
              ? "Lo que falta pedir no tiene proveedor: asignáselo para poder pedirlo."
              : "No hay nada nuevo para pedir: lo vendido ya se pidió y espera la compra."
          }
        />
      )}

      {/* Cerrados: la pantalla es una lista de pedidos, uno por proveedor, y
          se abre el que se va a mandar. Si hay uno solo, abierto. */}
      <div className="space-y-2">
        {grupos.map((g) => (
          <GrupoProveedor
            key={g.proveedorId}
            grupo={g}
            abiertoInicial={grupos.length === 1}
            cantidades={cantidades}
            onCambiar={cambiar}
            onPedir={() => pedir(g)}
          />
        ))}
      </div>

      {sinProveedor ? <SinProveedor grupo={sinProveedor} onAbrirFicha={onAbrirFicha} /> : null}

      {esperando.length > 0 ? <Esperando filas={esperando} onAnular={onAnularPedido} /> : null}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Resumen
// ─────────────────────────────────────────────────────────────────────────────

/**
 * La primera lectura: cuanto hay para pedir y cuanto sale. El total va en las
 * dos monedas porque abarca proveedores que cobran en una y en otra; es el
 * mismo pedido leido de dos formas, nunca una suma.
 */
function Resumen({
  productos,
  unidades,
  proveedores,
  urgentes,
  esperando,
  sinProveedor,
  ars,
  usd,
  hayCambios,
  onRestablecer,
}: {
  /** Los mismos que cuenta la barra: todas las filas, con y sin proveedor. */
  productos: number;
  unidades: number;
  proveedores: number;
  urgentes: number;
  esperando: number;
  sinProveedor: number;
  ars: number;
  usd: number;
  hayCambios: boolean;
  onRestablecer: () => void;
}): JSX.Element {
  return (
    <Card padding="compacto" className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3">
      <div className="space-y-1">
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          Para pedir
          <Tooltip>
            <TooltipTrigger asChild>
              <IconButton
                icono={Info}
                tamano="sm"
                aria-label="De dónde sale esta lista"
                className="size-6 text-muted-foreground sm:size-6"
              />
            </TooltipTrigger>
            <TooltipContent className="max-w-sm font-normal leading-relaxed">
              Lo que se vendió en los últimos 30 días y no se volvió a comprar. Cargar la factura en
              Compras lo saca de la lista; lo devuelto con nota de crédito no cuenta. Al pedir por
              WhatsApp queda anotado y espera la compra. El costo es una estimación con el costo de
              reposición cargado; el precio de verdad lo pone la lista del día.
            </TooltipContent>
          </Tooltip>
        </p>
        <p className="text-sm">
          <strong className="text-2xl font-semibold tabular-nums">
            {formatearNumero(unidades, 0)}
          </strong>{" "}
          {unidades === 1 ? "unidad" : "unidades"} a {proveedores}{" "}
          {proveedores === 1 ? "proveedor" : "proveedores"}
        </p>
        <p className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {/* El numero de la barra de arriba, dicho aca con la misma frase: la
              barra cuenta productos y este resumen unidades, y sin esto el
              numero de la barra no aparecia en ningun lado de la pantalla. */}
          <span>
            {productos} {productos === 1 ? "producto para reponer" : "productos para reponer"}
          </span>
          {urgentes > 0 ? (
            <span className="inline-flex items-center gap-1 font-medium text-warning">
              <AlertTriangle className="size-3.5" aria-hidden="true" />
              {urgentes} {urgentes === 1 ? "urgente" : "urgentes"}
            </span>
          ) : null}
          {esperando > 0 ? (
            <span className="inline-flex items-center gap-1">
              <Clock className="size-3.5" aria-hidden="true" />
              {esperando} {esperando === 1 ? "pedido esperando" : "pedidos esperando"}
            </span>
          ) : null}
          {sinProveedor > 0 ? (
            <span className="inline-flex items-center gap-1">
              <UserX className="size-3.5" aria-hidden="true" />
              {sinProveedor} sin proveedor
            </span>
          ) : null}
        </p>
      </div>

      <div className="space-y-1 sm:text-right">
        <p className="text-xs text-muted-foreground">Costo estimado</p>
        <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1 sm:justify-end">
          <Importe monto={ars} moneda="ARS" tamano="lg" className="font-semibold" />
          <span className="text-xs text-muted-foreground">o</span>
          <Importe monto={usd} moneda="USD" tamano="lg" className="font-semibold" />
        </p>
        {hayCambios ? (
          <Button type="button" variant="link" size="sm" onClick={onRestablecer}>
            <RotateCcw aria-hidden="true" />
            Volver a lo sugerido
          </Button>
        ) : null}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Un proveedor
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Cuantas pedir. Texto y no `type="number"`: la rueda del mouse cambiaba el
 * valor por accidente (ver `CampoCantidad`). Solo unidades enteras; vacio vale
 * 0, que saca el renglon del pedido sin borrarlo.
 */
function CantidadPedido({
  fila,
  valor,
  onCambiar,
}: {
  fila: FilaReposicion;
  valor: number;
  onCambiar: (f: FilaReposicion, n: number) => void;
}): JSX.Element {
  function alCambiar(e: ChangeEvent<HTMLInputElement>): void {
    const limpio = e.target.value.replace(/\D/g, "").slice(0, 4);
    onCambiar(fila, limpio === "" ? 0 : Number(limpio));
  }

  return (
    <Input
      inputMode="numeric"
      value={String(valor)}
      onChange={alCambiar}
      onFocus={(e) => e.target.select()}
      aria-label={`Cuántas pedir de ${nombreCompleto(fila)}`}
      className="h-9 w-16 text-right font-semibold tabular-nums sm:h-8"
    />
  );
}

/**
 * Cuanto queda en el estante y para cuantos dias alcanza. Lo urgente se lee en
 * color Y en palabras; lo demas, en palabras nada mas.
 */
function Stock({
  fila,
  enLinea = false,
}: {
  fila: FilaReposicion;
  enLinea?: boolean;
}): JSX.Element {
  const hay = aNumero(fila.stock_actual);
  const numero = formatearNumero(hay, 0);
  const aviso = alcanceLegible(fila);
  if (!fila.urgente) {
    if (enLinea) {
      return <span>{hay <= 0 ? aviso : aviso ? `hay ${numero}, ${aviso}` : `hay ${numero}`}</span>;
    }
    return (
      <span className="inline-flex flex-col items-end">
        <span>{numero}</span>
        {aviso ? <span className="text-xs text-muted-foreground">{aviso}</span> : null}
      </span>
    );
  }
  if (enLinea) {
    return (
      <span className="inline-flex items-center gap-1 font-medium text-warning">
        <AlertTriangle className="size-3" aria-hidden="true" />
        {hay <= 0 ? aviso : `hay ${numero}, ${aviso}`}
      </span>
    );
  }
  return (
    <span className="inline-flex flex-col items-end text-warning">
      <span className="font-semibold">{numero}</span>
      <span className="inline-flex items-center gap-1 text-xs">
        <AlertTriangle className="size-3" aria-hidden="true" />
        {aviso}
      </span>
    </span>
  );
}

/** A que ritmo se vende: «3,5/sem · rota mucho», o lo vendido sin historia. */
function Ritmo({
  fila,
  enLinea = false,
}: {
  fila: FilaReposicion;
  enLinea?: boolean;
}): JSX.Element {
  const { valor, detalle } = ritmoLegible(fila);
  if (enLinea) {
    return <span>{fila.con_historia ? `vende ${valor}` : `${detalle} ${valor}`}</span>;
  }
  return (
    <span className="inline-flex flex-col items-end">
      <span>{valor}</span>
      <span className="text-xs text-muted-foreground">{detalle}</span>
    </span>
  );
}

/** El boton que abre WhatsApp con el pedido, o por que no se puede. */
function PedirPorWhatsapp({
  grupo,
  cantidades,
  onPedir,
}: {
  grupo: GrupoReposicion;
  cantidades: CantidadesPedido;
  onPedir: () => void;
}): JSX.Element {
  if (grupo.proveedorId === null) {
    return (
      <span className="text-xs text-muted-foreground">
        Asignales proveedor en la ficha para poder pedirlos.
      </span>
    );
  }
  const link =
    grupo.unidades > 0
      ? linkWhatsapp(
          numeroWhatsapp(grupo.telefono),
          mensajePedido(grupo, nombreCompleto, cantidades),
        )
      : null;
  if (!link) {
    return (
      <span className="text-xs text-muted-foreground">
        {grupo.unidades > 0 ? "Sin teléfono cargado" : "Nada para pedir"}
      </span>
    );
  }
  return (
    <Button asChild size="sm">
      <a href={link} target="_blank" rel="noreferrer" onClick={onPedir}>
        <MessageCircle aria-hidden="true" />
        Pedir por WhatsApp
      </a>
    </Button>
  );
}

/**
 * La cabecera de un pedido: se lee cerrada. Nombre, cuantos productos y
 * unidades, cuantos urgentes, el total y el boton. Abrirla muestra el detalle
 * para corregir cantidades antes de mandarlo.
 */
function GrupoProveedor({
  grupo,
  abiertoInicial,
  cantidades,
  onCambiar,
  onPedir,
}: {
  grupo: GrupoReposicion;
  abiertoInicial: boolean;
  cantidades: CantidadesPedido;
  onCambiar: (f: FilaReposicion, n: number) => void;
  onPedir: () => void;
}): JSX.Element {
  const [abierto, setAbierto] = useState(abiertoInicial);
  const Chevron = abierto ? ChevronDown : ChevronRight;
  const totalLista = totalEnLista(grupo);
  const productos = grupo.filas.length;
  const resumen = `${productos} ${productos === 1 ? "producto" : "productos"} · ${formatearNumero(grupo.unidades, 0)} u.`;

  return (
    <Card padding="none" className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 px-4 py-3">
        <Button
          type="button"
          variant="ghost"
          onClick={() => setAbierto((a) => !a)}
          aria-expanded={abierto}
          className="-mx-2 h-auto min-w-0 justify-start gap-2 whitespace-normal px-2 py-1 text-left font-normal sm:h-auto"
        >
          <Chevron className="text-muted-foreground" aria-hidden="true" />
          <span className="min-w-0">
            <span className="block text-base font-semibold leading-tight">{grupo.proveedor}</span>
            <span className="flex flex-wrap gap-x-2 text-xs tabular-nums text-muted-foreground">
              <span>{resumen}</span>
              {grupo.urgentes > 0 ? (
                <span className="inline-flex items-center gap-1 font-medium text-warning">
                  <AlertTriangle className="size-3" aria-hidden="true" />
                  {grupo.urgentes} {grupo.urgentes === 1 ? "urgente" : "urgentes"}
                </span>
              ) : null}
            </span>
          </span>
        </Button>
        <div className="flex w-full items-center justify-between gap-4 sm:w-auto">
          <Importe monto={totalLista.monto} moneda={totalLista.moneda} className="font-semibold" />
          <PedirPorWhatsapp grupo={grupo} cantidades={cantidades} onPedir={onPedir} />
        </div>
      </div>

      {abierto ? (
        <>
          <div className="hidden border-t border-border md:block">
            <Table densidad="normal">
              <TableHeader>
                <TableRow>
                  <TableHead>Producto</TableHead>
                  <TableHead className="w-28 text-right">Se vende</TableHead>
                  <TableHead className="w-28 text-right">Hay</TableHead>
                  <TableHead className="w-24 text-right">Pedir</TableHead>
                  <TableHead className="w-40 text-right">Subtotal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {grupo.filas.map((f) => {
                  const n = cantidadAPedir(f, cantidades);
                  const sub = subtotalRenglon(f, cantidades);
                  const ideal = aNumero(f.stock_ideal);
                  return (
                    <TableRow key={f.producto_id} className={cn(n === 0 && "opacity-50")}>
                      <TableCell>
                        <span className="block text-sm">{nombreCompleto(f)}</span>
                        <Codigo className="block text-xs text-muted-foreground">
                          {f.codigo_externo ?? f.codigo_interno}
                        </Codigo>
                      </TableCell>
                      <TableCellImporte className="text-sm tabular-nums">
                        <Ritmo fila={f} />
                      </TableCellImporte>
                      <TableCellImporte className="text-sm tabular-nums">
                        <Stock fila={f} />
                        {ideal > 0 ? (
                          <span className="block text-xs text-muted-foreground">
                            ideal {formatearNumero(ideal, 0)}
                          </span>
                        ) : null}
                      </TableCellImporte>
                      <TableCell className="text-right">
                        <span className="inline-flex justify-end">
                          <CantidadPedido fila={f} valor={n} onCambiar={onCambiar} />
                        </span>
                      </TableCell>
                      <TableCellImporte>
                        <span className="inline-flex flex-col items-end gap-0.5">
                          <Importe monto={sub.monto} moneda={sub.moneda} />
                          <span className="inline-flex items-baseline gap-1 text-xs text-muted-foreground">
                            ×
                            <Importe
                              monto={aNumero(f.costo)}
                              moneda={f.costo_moneda}
                              codigo={false}
                              tamano="sm"
                              className="font-normal text-muted-foreground"
                            />
                          </span>
                        </span>
                      </TableCellImporte>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter className="static">
                <TableRow>
                  <TableCell colSpan={3} className="text-sm">
                    Total del pedido
                  </TableCell>
                  <TableCellImporte className="text-sm tabular-nums">
                    {formatearNumero(grupo.unidades, 0)} u.
                  </TableCellImporte>
                  <TableCellImporte
                    monto={totalLista.monto}
                    moneda={totalLista.moneda}
                    className="font-semibold"
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </div>

          {/* Mobile: renglones separados por linea dentro de la tarjeta (una
              Card dentro de otra es un anti-patron), y el total al pie. */}
          <ul className="divide-y divide-border border-t border-border md:hidden">
            {grupo.filas.map((f) => {
              const n = cantidadAPedir(f, cantidades);
              const sub = subtotalRenglon(f, cantidades);
              return (
                <li
                  key={f.producto_id}
                  className={cn("flex items-center gap-3 px-4 py-3", n === 0 && "opacity-50")}
                >
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="line-clamp-2 text-sm">{nombreCompleto(f)}</span>
                    <span className="flex flex-wrap gap-x-2 text-xs tabular-nums text-muted-foreground">
                      <Ritmo fila={f} enLinea />
                      <span aria-hidden="true">·</span>
                      <Stock fila={f} enLinea />
                    </span>
                    <Importe monto={sub.monto} moneda={sub.moneda} tamano="sm" />
                  </div>
                  <CantidadPedido fila={f} valor={n} onCambiar={onCambiar} />
                </li>
              );
            })}
            <li className="flex items-center justify-between gap-3 bg-muted px-4 py-3 text-sm font-medium">
              <span>Total del pedido</span>
              <Importe
                monto={totalLista.monto}
                moneda={totalLista.moneda}
                className="font-semibold"
              />
            </li>
          </ul>
        </>
      ) : null}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sin proveedor
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lo vendido que no se le puede pedir a nadie porque la ficha no dice a quien
 * se le compra. No se esconde: mientras no tenga proveedor va a seguir aca, y
 * lo que hace falta es un clic a la ficha, no un pedido.
 */
function SinProveedor({
  grupo,
  onAbrirFicha,
}: {
  grupo: GrupoReposicion;
  onAbrirFicha?: (productoId: number) => void;
}): JSX.Element {
  return (
    <section className="space-y-2 pt-2">
      <TituloSeccion
        nivel="etiqueta"
        icono={UserX}
        contador={grupo.filas.length}
        descripcion="Hay que reponerlos pero no se pueden pedir: la ficha no dice a quién se le compran. Asignales proveedor y pasan a su pedido."
      >
        Sin proveedor
      </TituloSeccion>
      <Card padding="none" className="overflow-hidden">
        <ul className="divide-y divide-border">
          {grupo.filas.map((f) => (
            <li
              key={f.producto_id}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3"
            >
              <div className="w-full min-w-0 sm:w-auto sm:flex-1">
                <span className="block text-sm">{nombreCompleto(f)}</span>
                <span className="flex flex-wrap gap-x-2 text-xs tabular-nums text-muted-foreground">
                  <Ritmo fila={f} enLinea />
                  <span aria-hidden="true">·</span>
                  <Stock fila={f} enLinea />
                </span>
              </div>
              {onAbrirFicha ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onAbrirFicha(f.producto_id)}
                  aria-label={`Asignar proveedor a ${nombreCompleto(f)}`}
                >
                  Asignar proveedor
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Esperando la compra
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lo pedido que todavia no llego. Aparte de las tarjetas, porque no hay nada
 * que hacer con eso salvo esperar, reclamarlo si se demora o deshacerlo si se
 * anoto por error.
 */
function Esperando({
  filas,
  onAnular,
}: {
  filas: ReadonlyArray<FilaReposicion>;
  onAnular?: (productoId: number) => void;
}): JSX.Element {
  const ordenadas = [...filas].sort((a, b) => diasDelPedido(b) - diasDelPedido(a));
  return (
    <section className="space-y-2 pt-2">
      <TituloSeccion
        nivel="etiqueta"
        icono={Clock}
        contador={filas.length}
        descripcion="Pedidos por WhatsApp. Salen de acá cuando se carga la compra."
      >
        Esperando la compra
      </TituloSeccion>
      <Card padding="none" className="overflow-hidden">
        <ul className="divide-y divide-border">
          {ordenadas.map((f) => {
            const dias = diasDelPedido(f);
            const demorado = dias >= DIAS_PEDIDO_DEMORADO;
            return (
              <li
                key={f.producto_id}
                className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-3"
              >
                <div className="w-full min-w-0 sm:w-auto sm:flex-1">
                  <span className="block text-sm">{nombreCompleto(f)}</span>
                  <span className="block text-xs tabular-nums text-muted-foreground">
                    {f.proveedor} · {formatearNumero(aNumero(f.pedido_cantidad), 0)} u. · pedido el{" "}
                    {formatearFecha(f.pedido_at)}
                  </span>
                </div>
                <div className="-mr-3 flex w-full items-center justify-between gap-3 sm:mr-0 sm:w-auto">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 text-xs",
                      demorado ? "font-medium text-warning" : "text-muted-foreground",
                    )}
                  >
                    {demorado ? <AlertTriangle className="size-3" aria-hidden="true" /> : null}
                    {dias === 0
                      ? "hoy"
                      : `hace ${dias} ${dias === 1 ? "día" : "días"}${demorado ? ", reclamalo" : ""}`}
                  </span>
                  {onAnular ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onAnular(f.producto_id)}
                      aria-label={`Deshacer el pedido de ${nombreCompleto(f)}`}
                    >
                      Deshacer
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      </Card>
    </section>
  );
}
